import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { MapPinIcon, ShieldCheckIcon, TargetIcon, UserIcon, ArrowRightIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
export function AboutPage() {
  return <div className="bg-viridia-black min-h-screen pt-24" data-id="element-294">
      <SEOHead title="About Viridia Analytics | Phoenix Marketing Agency | Kevin Rogers, Founder" description="Meet Kevin Rogers, founder of Viridia Analytics. A Phoenix-based, owner-operated marketing agency with insider expertise from Yelp and top ad platforms, built for local service businesses." canonical="/about" keywords="about Viridia Analytics, Kevin Rogers, Phoenix marketing agency, Yelp insider, local business marketing, owner-operated agency" data-id="element-295" />
      {/* HERO SECTION */}
      <section className="relative py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center overflow-hidden" data-id="element-296">
        {/* Subtle Grid Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" data-id="element-297"></div>

        <ScrollReveal className="relative z-10" data-id="element-298">
          <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-viridia/10 text-viridia text-sm font-bold tracking-widest uppercase mb-8 border border-viridia/20" data-id="element-299">
            ESTABLISHED 2025
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 tracking-tight" data-id="element-300">
            Crafting the{' '}
            <span className="text-viridia italic" data-id="element-301">Future of Brand.</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed" data-id="element-302">
            We bridge the gap between technical precision and human emotion to
            build legacies that last.
          </p>
        </ScrollReveal>
      </section>

      {/* FOUNDER / LEADERSHIP SECTION */}
      <section className="py-24 bg-viridia-light text-gray-900" data-id="element-303">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-304">
          <ScrollReveal data-id="element-305">
            <div className="mb-16" data-id="element-306">
              <span className="text-viridia font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-307">
                OUR LEADERSHIP
              </span>
              <h2 className="text-4xl md:text-5xl font-bold" data-id="element-308">
                Driving Growth Through Data
              </h2>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start" data-id="element-309">
            <ScrollReveal direction="right" className="lg:col-span-4" data-id="element-310">
              <div className="aspect-square rounded-2xl border-4 border-viridia/20 shadow-2xl relative overflow-hidden group" data-id="element-311">
                <img src="https://cdn.magicpatterns.com/uploads/c8uTYps8rnfBJSvNqFWMAq/Kevheadshot3.jpg" alt="Kevin Rogers — Founder & CEO, Viridia Analytics" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-312" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" data-id="element-313"></div>
              </div>
            </ScrollReveal>

            <ScrollReveal direction="left" delay={0.2} className="lg:col-span-8" data-id="element-314">
              <div className="prose prose-lg prose-gray max-w-none" data-id="element-315">
                <h3 className="text-3xl font-bold text-gray-900 mb-1" data-id="element-316">
                  Kevin Rogers
                </h3>
                <p className="text-viridia font-semibold text-lg mb-8" data-id="element-317">
                  Founder & CEO, Viridia Analytics
                </p>

                <p className="text-gray-600 leading-relaxed mb-6" data-id="element-318">
                  After years working inside the advertising industry —
                  including time at platforms like Yelp — I saw firsthand how
                  local businesses were being underserved. They were spending
                  money on ads but had no system to capture, follow up, or
                  convert those leads.
                </p>
                <p className="text-gray-600 leading-relaxed mb-6" data-id="element-319">
                  That's why I started Viridia Analytics. We're not a big agency
                  trying to upsell you on services you don't need. We're a
                  Phoenix-based, owner-operated team that builds real systems —
                  ads, AI lead conversion, and CRM workflows — designed
                  specifically for service businesses that want to grow without
                  the chaos.
                </p>
                <div className="mt-8 p-6 bg-gray-50 rounded-xl border border-gray-100 border-l-4 border-l-viridia" data-id="element-320">
                  <p className="text-gray-900 font-medium italic m-0" data-id="element-321">
                    "Every client gets my direct attention. No account managers,
                    no runaround."
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* VALUES SECTION */}
      <section className="py-24 bg-viridia-black" data-id="element-322">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-323">
          <ScrollReveal data-id="element-324">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-16 text-center" data-id="element-325">
              Why Viridia?
            </h2>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-326">
            <ScrollReveal delay={0.1} data-id="element-327">
              <div className="bg-viridia-surface p-8 rounded-2xl border border-gray-800 hover:border-viridia/50 transition-colors duration-300 h-full group" data-id="element-328">
                <div className="w-14 h-14 bg-viridia/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-329">
                  <MapPinIcon className="w-7 h-7 text-viridia" data-id="element-330" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-331">
                  Phoenix Local
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-332">
                  We're based in Phoenix and specialize in the local market. We
                  understand the seasonality, competition, and customer behavior
                  unique to Arizona service businesses.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-333">
              <div className="bg-viridia-surface p-8 rounded-2xl border border-gray-800 hover:border-viridia/50 transition-colors duration-300 h-full group" data-id="element-334">
                <div className="w-14 h-14 bg-viridia/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-335">
                  <ShieldCheckIcon className="w-7 h-7 text-viridia" data-id="element-336" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-337">
                  Platform Insiders
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-338">
                  Our team includes former employees from major ad platforms. We
                  don't guess — we know exactly how the algorithms work and how
                  to maximize your spend.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.3} data-id="element-339">
              <div className="bg-viridia-surface p-8 rounded-2xl border border-gray-800 hover:border-viridia/50 transition-colors duration-300 h-full group" data-id="element-340">
                <div className="w-14 h-14 bg-viridia/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-341">
                  <TargetIcon className="w-7 h-7 text-viridia" data-id="element-342" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-343">
                  No Wasted Leads
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-344">
                  Most agencies stop at getting you clicks. We build the entire
                  system — from ad to answer to booked job — so no lead falls
                  through the cracks.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.4} data-id="element-345">
              <div className="bg-viridia-surface p-8 rounded-2xl border border-gray-800 hover:border-viridia/50 transition-colors duration-300 h-full group" data-id="element-346">
                <div className="w-14 h-14 bg-viridia/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-347">
                  <UserIcon className="w-7 h-7 text-viridia" data-id="element-348" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-349">
                  Owner-Operated
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-350">
                  You work directly with the founder. No layers of account
                  managers. Fast decisions, real accountability, and someone who
                  actually cares about your results.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* MISSION SECTION */}
      <section className="py-24 bg-viridia-light text-gray-900" data-id="element-351">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-352">
          <ScrollReveal data-id="element-353">
            <TargetIcon className="w-12 h-12 text-viridia mx-auto mb-8 opacity-50" data-id="element-354" />
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-8" data-id="element-355">
              "Our mission is simple: help local service businesses in Phoenix
              turn their marketing into a predictable growth engine — with ads
              that work, systems that follow up, and reporting that shows
              exactly what's happening."
            </h2>
            <p className="text-lg font-semibold text-gray-500 uppercase tracking-widest" data-id="element-356">
              — Kevin Rogers, Founder
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia to-viridia-dark relative overflow-hidden" data-id="element-357">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-358"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-359">
          <ScrollReveal data-id="element-360">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-361">
              Let's Build Something Together
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-362">
              Whether you need ads, AI lead conversion, or a complete growth
              system — let's talk.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-lg bg-white text-viridia-dark hover:bg-gray-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1" data-id="element-363">
              Book a Call With Kevin <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-364" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}